/**
 * v7.56 - AddressAutocomplete usando EXCLUSIVAMENTE Places API (New).
 *
 * TICKET 207625 - O modo "manual" (quando o Web Component PlaceAutocompleteElement
 * do SDK nao carrega) agora mostra uma LISTA de predicoes via REST
 * `places:autocomplete`, pro cliente ESCOLHER o endereco em vez de digitar as
 * cegas e so geocodar no submit (lento/instavel). Resolve o gargalo de
 * "Validando endereco..." relatado. Ao selecionar, resolve lat/lng e chama
 * onPlaceSelected -> o checkout pula o geocoding de submit.
 *
 * Props:
 *   - value, onChange, onPlaceSelected, onStatusChange
 *   - placeholder, className, dataTestId, disabled
 * onPlaceSelected({ formattedAddress, lat, lng, components })
 * onStatusChange(status): 'idle' | 'loading' | 'ready_new' | 'manual'
 */
import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { loadGoogleMaps } from '@/lib/googleMaps';
import { autocompleteViaPlacesNew, geocodeViaPlacesNew } from '@/lib/placesNew';
import db from '@/db';
import { Loader2, MapPin, Search } from 'lucide-react';

export default function AddressAutocomplete({
  value = '',
  onChange,
  onPlaceSelected,
  onStatusChange,
  placeholder = 'Rua, numero, bairro, cidade',
  className = '',
  dataTestId = 'address-autocomplete-input',
  disabled = false,
}) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const elementRef = useRef(null);
  const debounceRef = useRef(null);
  const biasRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [predictions, setPredictions] = useState([]);
  // v7.83 (ticket 218940) -- Motivo da ultima falha de busca, para nao deixar
  // o cliente diante de um campo sem sugestoes e sem explicacao.
  const [lookupError, setLookupError] = useState(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (typeof onStatusChange === 'function') onStatusChange(status);
  }, [status, onStatusChange]);

  // TICKET 207625 - carrega o vies (coords do restaurante) uma vez pra
  // priorizar predicoes/geocoding ao redor da loja.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lat = (await db.config.get('restaurantLat'))?.value;
        const lng = (await db.config.get('restaurantLng'))?.value;
        if (!cancelled && Number.isFinite(lat) && Number.isFinite(lng)) {
          biasRef.current = { lat, lng };
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      const ok = await loadGoogleMaps();
      if (cancelled) return;
      if (!ok || !window.google?.maps?.importLibrary) {
        setStatus('manual');
        return;
      }
      try {
        // v7.83 (ticket 218940) -- `importLibrary` tambem pode nunca assentar.
        // Sem teto de espera o campo ficava preso em 'loading' para sempre; ao
        // expirar, o catch abaixo leva pro modo manual, que e utilizavel.
        const placesLib = await Promise.race([
          window.google.maps.importLibrary('places'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('PLACES_LIB_TIMEOUT')), 8000)),
        ]);
        if (cancelled) return;
        if (!placesLib?.PlaceAutocompleteElement) {
          setStatus('manual');
          return;
        }
        const el = new placesLib.PlaceAutocompleteElement({
          includedRegionCodes: ['br'],
        });
        el.style.width = '100%';

        el.addEventListener('gmp-select', async (ev) => {
          try {
            const prediction = ev.placePrediction || ev.detail?.placePrediction;
            if (!prediction) return;
            const place = prediction.toPlace();
            await place.fetchFields({
              fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
            });
            const formatted = place.formattedAddress || place.displayName || '';
            const lat = typeof place.location?.lat === 'function' ? place.location.lat() : place.location?.lat;
            const lng = typeof place.location?.lng === 'function' ? place.location.lng() : place.location?.lng;
            const components = (place.addressComponents || []).map((c) => ({
              long_name: c.longText || c.long_name || '',
              short_name: c.shortText || c.short_name || '',
              types: c.types || [],
            }));
            onChange?.(formatted);
            onPlaceSelected?.({ formattedAddress: formatted, lat, lng, components });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[GMAPS NEW] gmp-select handler falhou:', err);
          }
        });

        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(el);
        }
        elementRef.current = el;
        setStatus('ready_new');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[GMAPS NEW] PlaceAutocompleteElement falhou -> modo manual + predicoes REST:', err?.message || err);
        setStatus('manual');
      }
    })();

    return () => {
      cancelled = true;
      if (elementRef.current) {
        try { elementRef.current.remove(); } catch (_) { /* noop */ }
        elementRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup do debounce
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // TICKET 207625 - modo manual: predicoes debounced enquanto digita.
  const handleManualChange = (text) => {
    onChange?.(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (text || '').trim();
    if (q.length < 4) { setPredictions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await autocompleteViaPlacesNew(q, biasRef.current ? { bias: biasRef.current } : {});
        if (res.ok) {
          setPredictions((res.predictions || []).slice(0, 5));
          setLookupError(null);
        } else {
          // v7.83 (ticket 218940) -- antes a falha era engolida em silencio: o
          // cliente via um campo sem sugestoes e nenhuma pista do motivo, e
          // reescrevia o endereco indefinidamente. HTTP_403 aqui significa
          // chave do Google bloqueada para este dominio de acesso, que so o
          // dono do projeto no Google Cloud resolve.
          setPredictions([]);
          setLookupError(res.error || 'UNKNOWN');
        }
      } catch (err) {
        setPredictions([]);
        setLookupError(err?.message || 'NETWORK_ERROR');
      }
    }, 350);
  };

  const handleSelectPrediction = async (pred) => {
    if (resolving) return;
    setResolving(true);
    setPredictions([]);
    onChange?.(pred.text);
    try {
      const res = await geocodeViaPlacesNew(pred.text, biasRef.current ? { bias: biasRef.current } : {});
      if (res.ok && res.place) {
        onChange?.(res.place.formattedAddress || pred.text);
        onPlaceSelected?.({
          formattedAddress: res.place.formattedAddress || pred.text,
          lat: res.place.lat,
          lng: res.place.lng,
          components: res.place.components,
        });
      }
    } catch { /* fluxo de submit geocoda como fallback */ }
    finally { setResolving(false); }
  };

  // === RENDER ===

  if (status === 'ready_new') {
    return (
      <div className="relative">
        <div ref={containerRef} data-testid={dataTestId} className="kaven-pae-host">
          {/* PlaceAutocompleteElement injetado aqui */}
        </div>
        <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1">
          <MapPin size={10} /> Google Maps ativo - selecione da lista
        </p>
      </div>
    );
  }

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="relative">
        <Input
          ref={inputRef}
          data-testid={dataTestId}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className={className}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Loader2 size={16} className="animate-spin text-zinc-400" />
        </div>
      </div>
    );
  }

  // MANUAL: input + dropdown de predicoes (TICKET 207625)
  return (
    <div className="relative">
      <Input
        ref={inputRef}
        data-testid={dataTestId}
        value={value}
        disabled={disabled}
        onChange={(e) => handleManualChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-testid="address-autocomplete-manual">
        {resolving ? <Loader2 size={14} className="animate-spin text-blue-500" /> : <Search size={14} className="text-blue-500" />}
      </div>
      {lookupError && predictions.length === 0 && (
        <p data-testid="address-lookup-error" className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
          <MapPin size={11} className="shrink-0 mt-0.5" />
          <span>
            {/403|PERMISSION_DENIED|REFERRER/i.test(String(lookupError))
              ? 'Sugestões de endereço indisponíveis neste endereço de acesso. Digite o endereço completo (rua, número, bairro e cidade) — o pedido segue normalmente.'
              : 'Não foi possível buscar sugestões agora. Digite o endereço completo (rua, número, bairro e cidade) — o pedido segue normalmente.'}
          </span>
        </p>
      )}
      {predictions.length > 0 && (
        <ul data-testid="address-predictions" className="absolute z-50 left-0 right-0 mt-1 bg-white border-2 border-zinc-200 rounded-xl shadow-lg overflow-hidden max-h-64 overflow-y-auto">
          {predictions.map((p) => (
            <li key={p.placeId || p.text}>
              <button
                type="button"
                data-testid="address-prediction"
                onClick={() => handleSelectPrediction(p)}
                className="w-full text-left px-4 py-3 text-sm hover:bg-zinc-50 flex items-start gap-2 border-b border-zinc-100 last:border-0"
              >
                <MapPin size={14} className="text-blue-500 shrink-0 mt-0.5" />
                <span className="text-zinc-700">{p.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
